/**
 * Shared Sentry configuration utilities
 * Centralizes common Sentry settings and helpers
 */

import { Event, EventHint } from "@sentry/react";

/**
 * Get environment-specific sampling rates
 * Lower rates in production to reduce costs and improve performance
 */
export const getTracesSampleRate = (): number => {
  const env = import.meta.env.VITE_APP_ENVIRONMENT;
  
  switch (env) {
    case "prod":
      return 0.1; // 10% in production
    case "uat":
      return 0.3; // 30% in UAT
    case "qa":
      return 0.5; // 50% in QA
    case "dev":
    default:
      return 1.0; // 100% in dev
  }
};

/**
 * Get Sentry DSN from environment variable or fallback
 * DSN should be set in environment variables for security
 */
export const getSentryDSN = (): string => {
  // Try environment variable first (recommended)
  const envDSN = import.meta.env.VITE_SENTRY_DSN;
  if (envDSN) {
    return envDSN;
  }
  
  // Fallback to hardcoded DSN (for backward compatibility)
  // TODO: Move this to environment variable in all environments
  return "https://84c9423c71bb0104c231badb131d5d7a@o4509810351013888.ingest.us.sentry.io/4509905658511360";
};

/**
 * Filter sensitive data before sending to Sentry
 * Removes PII, tokens, and other sensitive information
 */
export const beforeSend = (event: Event, _hint: EventHint): Event | null => {
  // Don't send errors in development (optional - remove if you want dev errors)
  // if (import.meta.env.DEV) {
  //   return null;
  // }

  // Remove sensitive data from request bodies
  if (event.request?.data) {
    const data = event.request.data;
    
    // Remove common sensitive fields
    if (typeof data === "object" && data !== null && !Array.isArray(data)) {
      const sensitiveFields = [
        "password",
        "token",
        "accessToken",
        "refreshToken",
        "authorization",
        "apiKey",
        "secret",
        "ssn",
        "creditCard",
      ];
      
      sensitiveFields.forEach((field) => {
        if (field in data && typeof (data as Record<string, any>)[field] !== "undefined") {
          (data as Record<string, any>)[field] = "[Filtered]";
        }
      });
    }
  }

  // Remove sensitive data from extra context
  if (event.extra && typeof event.extra === "object") {
    const extra = event.extra as Record<string, any>;
    const sensitiveKeys = ["token", "password", "authorization"];
    sensitiveKeys.forEach((key) => {
      if (key in extra && typeof extra[key] !== "undefined") {
        extra[key] = "[Filtered]";
      }
    });
  }

  // Remove sensitive headers
  if (event.request?.headers && typeof event.request.headers === "object") {
    const sensitiveHeaders = ["authorization", "cookie", "x-api-key"];
    const headers = event.request.headers as Record<string, any>;
    sensitiveHeaders.forEach((header) => {
      const lowerHeader = header.toLowerCase();
      if (lowerHeader in headers && typeof headers[lowerHeader] !== "undefined") {
        headers[lowerHeader] = "[Filtered]";
      }
    });
  }

  return event;
};

/**
 * Standard Sentry tags used across the application
 */
export const STANDARD_TAGS = {
  CONTEXT: "context",
  SOURCE: "source",
  TENANT: "tenant",
  USER_ID: "userId",
  ERROR_TYPE: "error_type",
} as const;

/**
 * Standard Sentry contexts used across the application
 */
export const STANDARD_CONTEXTS = {
  SERVICE_WORKER: "service_worker",
  MAIN_THREAD: "main_thread",
  INDEXED_DB: "indexeddb",
} as const;

